import { gql, type DocumentNode } from "@apollo/client";

export interface EndpointConfig {
  staticFieldsQuery: DocumentNode;
  dynamicFieldsQuery: DocumentNode;
  endpointFieldsQuery: DocumentNode;
  staticKey: string;
  dynamicKey: string;
  endpointFieldsKey: string;
}

interface GenerateGetTicketEndpointKeysArgs {
  variables?: unknown;
  result?: unknown;
  error?: Error | null;
}

// Return shape mirrors the runtime: the caller's `result` is always wrapped as
// `{ data: result }` (or `{}` when omitted), and `error` is passed through
// as-is. Tightened so the returned object is assignable to Apollo's
// MockedResponse (whose `result` expects a FetchResult — `data` optional).
interface GeneratedTicketEndpointKeysMock {
  request: { query: DocumentNode; variables?: unknown };
  error?: Error;
  result?: { data?: unknown };
}

export const EXTERNAL_MANUAL_TICKET = gql`
  mutation openExternalTicket(
    $ticket_provider_id: String!
    $value_mapping: JSONString!
    $tickets_to_open: [ExternalTicketsToOpenInput]!
    $user_id: String
    $partner_external_id: String
  ) {
    open_tickets_from_external(
      ticket_provider_id: $ticket_provider_id
      value_mapping: $value_mapping
      tickets_to_open: $tickets_to_open
      user_id: $user_id
      partner_external_id: $partner_external_id
    ) {
      bulk_response
      rejected_collection_params
      timestamp
      ticket {
        id
        external_id
        finding_id
        link
        assignee
        status
        external_status
      }
    }
  }
`;

export const MANUAL_TICKET = gql`
  mutation openTicket(
    $filters_config: FiltersConfigInput!
    $ticket_provider_id: String!
    $value_mapping: JSONString
    $duedate: String
    $remediation_queue_id: String
  ) {
    open_ticket(
      filters_config: $filters_config
      ticket_provider_id: $ticket_provider_id
      remediation_queue_id: $remediation_queue_id
      value_mapping: $value_mapping
      duedate: $duedate
    ) {
      ticket {
        ticket_due_date_remaining_time
        ticket_due_date_status
        due_date
        id
        status
        ticket_provider {
          known_identifier
          type
        }
        link
        external_id
        external_status
      }
    }
  }
`;

export const FIELD_INPUT_FRAGMENT = gql`
  fragment fieldInputFragment on FieldInput {
    display_name
    field_name
    description
    field_input_type
    default_value
    required
    options {
      is_creatable
      values {
        id
        name
      }
    }
    query_data
    field_input_size
    disabled
  }
`;

export const MAPPER_FIELD_INPUT_FRAGMENT = gql`
  fragment mapperFieldInputFragment on MapperRowField {
    display_name
    field_name
    field_input_type
    default_value
    required
    options {
      is_creatable
      values {
        id
        name
      }
    }
    query_data
    icon
    disabled
  }
`;

export const GET_TICKET_ENDPOINT_KEYS = gql`
  ${FIELD_INPUT_FRAGMENT}
  query getTicketEndpointKeys(
    $ticket_provider_id: String
    $selected_keys: JSONString
    $partner_external_id: String
    $field_mapping_id: String
  ) {
    endpoint_keys(
      ticket_provider_id: $ticket_provider_id
      selected_keys: $selected_keys
      partner_external_id: $partner_external_id
      field_mapping_id: $field_mapping_id
    ) {
      is_final
      has_mapping
      fields_sections {
        name
        rows {
          fields {
            fieldDescendants: descendants
            fieldPrecedents: precedents
            skeleton
            is_primary
            ...fieldInputFragment
          }
        }
      }
    }
  }
`;

export const generateGetTicketEndpointKeys = ({
  variables = null,
  result = null,
  error = null,
}: GenerateGetTicketEndpointKeysArgs): GeneratedTicketEndpointKeysMock => {
  let GetTicketEndpointKeysMock = {
    request: {
      query: GET_TICKET_ENDPOINT_KEYS,
      variables,
    },
  };

  if (error) {
    return {
      ...GetTicketEndpointKeysMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...GetTicketEndpointKeysMock,
      result: { data: result },
    };
  }

  return {
    ...GetTicketEndpointKeysMock,
    result: {},
  };
};

export const GET_REMEDIATION_DATA_SUPPORTED_STATIC_FIELDS = gql`
  query static_remediation_variables {
    static_remediation_variables {
      variables
    }
  }
`;

export const GET_REMEDIATION_DATA_SUPPORTED_DYNAMIC_FIELDS = gql`
  query dynamic_remediation_variables {
    dynamic_remediation_variables {
      variables
    }
  }
`;

export const GET_MSEM_REMEDIATION_DATA_SUPPORTED_STATIC_FIELDS = gql`
  query msem_static_remediation_variables {
    msem_static_remediation_variables {
      variables
    }
  }
`;

export const GET_MSEM_REMEDIATION_DATA_SUPPORTED_DYNAMIC_FIELDS = gql`
  query msem_dynamic_remediation_variables {
    msem_dynamic_remediation_variables {
      variables
    }
  }
`;

export const GET_REMEDIATION_DATA_SUPPORTED_DYNAMIC_OPTIONS = gql`
  query dynamic_remediation_variables_options(
    $filters_config: FiltersConfigInput!
    $field_name: String!
    $search_string: String = ""
  ) {
    dynamic_remediation_variables_options(
      filters_config: $filters_config
      variable_name: $field_name
      search_string: $search_string
    ) {
      variables
    }
  }
`;

export const GET_TICKET_ENDPOINT_FIELDS = gql`
  ${FIELD_INPUT_FRAGMENT}
  ${MAPPER_FIELD_INPUT_FRAGMENT}
  query getTicketEndpointFields(
    $field_mapping_id: String
    $ticket_provider_id: String
    $selected_keys: JSONString
    $filters_config: FiltersConfigInput
    $endpoint_fields_context: String
    $initial_values: JSONString
    $disable_initial_values: Boolean
    $partner_external_id: String
    $ticket_template_id: Int
    $show_template_fields: Boolean = true
  ) {
    endpoint_fields(
      filters_config: $filters_config
      field_mapping_id: $field_mapping_id
      ticket_provider_id: $ticket_provider_id
      selected_keys: $selected_keys
      endpoint_fields_context: $endpoint_fields_context
      initial_values: $initial_values
      disable_initial_values: $disable_initial_values
      partner_external_id: $partner_external_id
      ticket_template_id: $ticket_template_id
    ) {
      fields_sections {
        name
        description
        rows {
          fields {
            mapping_data {
              columns {
                field_from {
                  display_name
                  icon
                }
                field_to {
                  display_name
                  icon
                }
              }
              rows {
                field_from {
                  ...mapperFieldInputFragment
                }
                field_to {
                  ...mapperFieldInputFragment
                }
              }
            }
            ...fieldInputFragment
          }
        }
      }
      ticket_template_fields @include(if: $show_template_fields) {
        display_name
        field_name
        display_value
        value
        field_type
        is_compatible
      }
    }
  }
`;

export const GET_MSEM_TICKET_ENDPOINT_FIELDS = gql`
  ${FIELD_INPUT_FRAGMENT}
  ${MAPPER_FIELD_INPUT_FRAGMENT}
  query getMsemTicketEndpointFields(
    $field_mapping_id: String
    $ticket_provider_id: String
    $selected_keys: JSONString
    $filters_config: FiltersConfigInput
    $endpoint_fields_context: String
    $initial_values: JSONString
    $disable_initial_values: Boolean
    $partner_external_id: String
    $ticket_template_id: Int
    $show_template_fields: Boolean = true
  ) {
    msem_endpoint_fields(
      filters_config: $filters_config
      field_mapping_id: $field_mapping_id
      ticket_provider_id: $ticket_provider_id
      selected_keys: $selected_keys
      endpoint_fields_context: $endpoint_fields_context
      initial_values: $initial_values
      disable_initial_values: $disable_initial_values
      partner_external_id: $partner_external_id
      ticket_template_id: $ticket_template_id
    ) {
      fields_sections {
        name
        description
        rows {
          fields {
            mapping_data {
              columns {
                field_from {
                  display_name
                  icon
                }
                field_to {
                  display_name
                  icon
                }
              }
              rows {
                field_from {
                  ...mapperFieldInputFragment
                }
                field_to {
                  ...mapperFieldInputFragment
                }
              }
            }
            ...fieldInputFragment
          }
        }
      }
      ticket_template_fields @include(if: $show_template_fields) {
        display_name
        field_name
        display_value
        value
        field_type
        is_compatible
      }
    }
  }
`;

export const DEFAULT_ENDPOINT_CONFIG: EndpointConfig = {
  staticFieldsQuery: GET_REMEDIATION_DATA_SUPPORTED_STATIC_FIELDS,
  dynamicFieldsQuery: GET_REMEDIATION_DATA_SUPPORTED_DYNAMIC_FIELDS,
  endpointFieldsQuery: GET_TICKET_ENDPOINT_FIELDS,
  staticKey: "static_remediation_variables",
  dynamicKey: "dynamic_remediation_variables",
  endpointFieldsKey: "endpoint_fields",
};

export const MSEM_ENDPOINT_CONFIG: EndpointConfig = {
  staticFieldsQuery: GET_MSEM_REMEDIATION_DATA_SUPPORTED_STATIC_FIELDS,
  dynamicFieldsQuery: GET_MSEM_REMEDIATION_DATA_SUPPORTED_DYNAMIC_FIELDS,
  endpointFieldsQuery: GET_MSEM_TICKET_ENDPOINT_FIELDS,
  staticKey: "msem_static_remediation_variables",
  dynamicKey: "msem_dynamic_remediation_variables",
  endpointFieldsKey: "msem_endpoint_fields",
};

export const GET_TICKET_ENDPOINT_COLUMNS = gql`
  ${FIELD_INPUT_FRAGMENT}
  ${MAPPER_FIELD_INPUT_FRAGMENT}
  query getTicketEndpointColumns(
    $ticket_provider_id: String
    $selected_keys: JSONString
  ) {
    endpoint_columns(
      ticket_provider_id: $ticket_provider_id
      selected_keys: $selected_keys
    ) {
      fields_sections {
        name
        display_name
        description
        is_collapsable
        rows {
          fields {
            mapping_data {
              columns {
                field_from {
                  display_name
                  icon
                }
                field_to {
                  display_name
                  icon
                }
              }
              rows {
                field_from {
                  ...mapperFieldInputFragment
                }
                field_to {
                  ...mapperFieldInputFragment
                }
              }
            }
            ...fieldInputFragment
          }
        }
      }
    }
  }
`;

export const GET_TICKET_ENDPOINT_COLUMNS_VALUES = gql`
  ${FIELD_INPUT_FRAGMENT}
  ${MAPPER_FIELD_INPUT_FRAGMENT}
  query getTicketEndpointColumnsValues(
    $ticket_provider_id: String
    $selected_keys: JSONString
    $selected_columns: JSONString
  ) {
    field_mapping_definition_fields(
      ticket_provider_id: $ticket_provider_id
      selected_keys: $selected_keys
      selected_columns: $selected_columns
    ) {
      fields_sections {
        name
        display_name
        description
        is_collapsable
        rows {
          fields {
            mapping_data {
              columns {
                field_from {
                  display_name
                  icon
                }
                field_to {
                  display_name
                  icon
                }
              }
              rows {
                field_from {
                  ...mapperFieldInputFragment
                }
                field_to {
                  ...mapperFieldInputFragment
                }
              }
            }
            ...fieldInputFragment
          }
        }
      }
    }
  }
`;

export const GET_QUERY_FIELD = gql`
  query get_query_fields(
    $ticket_provider_id: String!
    $field_name: String!
    $params: JSONString!
  ) {
    get_query_fields(
      ticket_provider_id: $ticket_provider_id
      field_name: $field_name
      params: $params
    ) {
      values {
        id
        name
      }
    }
  }
`;

export const SAVE_MAPPING = gql`
  mutation saveMapping(
    $selected_columns: JSONString
    $selected_keys: JSONString!
    $selected_mapping_fields: JSONString!
    $ticket_provider_id: String!
  ) {
    update_field_mapping_definition(
      selected_columns: $selected_columns
      selected_keys: $selected_keys
      selected_mapping_fields: $selected_mapping_fields
      ticket_provider_id: $ticket_provider_id
    ) {
      ok
    }
  }
`;

export const GET_PARTNER_REMEDIATION_QUEUE = gql`
  query getPartnerRemediationQueue($partner_external_id: String!) {
    partner_remediation_queue(partner_external_id: $partner_external_id) {
      queue_size
    }
  }
`;

export const GET_TICKET_MANAGERS = gql`
  query getTicketManagers(
    $providers_query_context: EndpointFieldsContext
    $partner_external_id: String
    $filters: TicketManagerFilters
  ) {
    ticket_managers(
      providers_query_context: $providers_query_context
      partner_external_id: $partner_external_id
      filters: $filters
    ) {
      type
      ticket_provider_definition_id
      endpoint_type
      implements_column_mapping
      must_have_mapping
      friendly_name
      providers {
        id
        display
        known_identifier
      }
      attachments_supported
      status
    }
  }
`;

export const GET_TICKET_TEMPLATES_SUMMARY = gql`
  query getTicketTemplatesSummary(
    $ticket_provider_id: String
    $has_ticket_provider: Boolean = false
  ) {
    ticket_templates_summary(ticket_provider_id: $ticket_provider_id) {
      edges {
        node {
          id
          name
          description
          ticket_provider_type @skip(if: $has_ticket_provider)
          created_by_username @skip(if: $has_ticket_provider)
          created_time @skip(if: $has_ticket_provider)
          queues @skip(if: $has_ticket_provider) {
            id
            name
          }
        }
      }
    }
  }
`;

export const GET_TICKET_TEMPLATE = gql`
  query getTicketTemplate(
    $ticket_provider_id: String!
    $template_id: Int!
    $selected_keys: JSONString!
  ) {
    ticket_template(
      ticket_provider_id: $ticket_provider_id
      template_id: $template_id
      selected_keys: $selected_keys
    ) {
      id
      name
      created_by
      created_time
      description
      is_compatible
      fields {
        display_name
        field_name
        display_value
        value
        is_compatible
      }
    }
  }
`;

export const CREATE_TICKET_TEMPLATE = gql`
  mutation createTicketTemplate(
    $name: String!
    $description: String
    $ticket_provider_id: String!
    $fields: [TicketTemplateFieldInput]!
    $selected_keys: JSONString
  ) {
    create_ticket_template(
      name: $name
      description: $description
      ticket_provider_id: $ticket_provider_id
      fields: $fields
      selected_keys: $selected_keys
    ) {
      ticket_template {
        id
      }
    }
  }
`;

export const DELETE_TICKET_TEMPLATE = gql`
  mutation deleteTicketTemplate($template_id: Int!) {
    delete_ticket_template(template_id: $template_id) {
      ok
    }
  }
`;

export const GET_TICKET_TEMPLATE_SUMMARY = gql`
  query getTicketTemplateSummary($template_id: Int!) {
    ticket_template_summary(template_id: $template_id) {
      id
      name
      description
      ticket_provider_type
      ticket_provider_display_name
      keys {
        display_name
        display_value
      }
      queues {
        id
        name
        status
        updated_time
        updated_by
        last_synced
        status_details {
          errors {
            error_type
            error_message
          }
        }
      }
    }
  }
`;
